import { useEffect, useState } from 'react';
import WorkspaceTransitionSurface from './WorkspaceTransitionSurface';
import type { WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';

export type WorkspaceLoadGuardProps = {
  title?: string;
  subtitle?: string;
  previewMode?: boolean;
  showHeaderCopy?: boolean;
  timeoutMs?: number;
  slowThresholdMs?: number;
  attempt?: number;
  watch?: unknown;
  onRetry?: () => void;
  onOpenAnyway?: () => void;
  isReady?: boolean;
  onComplete?: () => void;
  error?: string | null;
  isTimedOut?: boolean;
  isSlow?: boolean;
  isAuthenticated?: boolean;
  onSignOut?: () => void;
  progress?: number | null;
  whiteLabel?: WhiteLabelAppBranding | null;
  neutralBrandFallback?: boolean;
  [key: string]: unknown;
};

export default function WorkspaceLoadGuard({
  title,
  subtitle,
  previewMode,
  showHeaderCopy,
  timeoutMs = 8000,
  slowThresholdMs,
  attempt = 0,
  onRetry,
  isReady = false,
  onComplete,
  error = null,
  isTimedOut = false,
  isSlow = false,
  isAuthenticated = false,
  onSignOut,
  progress,
  whiteLabel = null,
  neutralBrandFallback = false,
}: WorkspaceLoadGuardProps) {
  const [localSlow, setLocalSlow] = useState(false);
  const activeSlowThresholdMs = slowThresholdMs ?? timeoutMs;

  useEffect(() => {
    setLocalSlow(false);

    if (isReady || error || isTimedOut) {
      return;
    }

    const timer = window.setTimeout(() => {
      setLocalSlow(true);
    }, activeSlowThresholdMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [activeSlowThresholdMs, attempt, error, isReady, isTimedOut]);

  const activeSlow = isSlow || localSlow;

  return (
    <WorkspaceTransitionSurface
      title={title}
      subtitle={subtitle}
      previewMode={previewMode}
      showHeaderCopy={showHeaderCopy}
      isReady={isReady}
      onComplete={onComplete}
      error={error}
      isTimedOut={isTimedOut}
      isSlow={activeSlow}
      isAuthenticated={isAuthenticated}
      onRetry={() => {
        setLocalSlow(false);
        if (onRetry) onRetry();
      }}
      onSignOut={onSignOut}
      progress={progress}
      whiteLabel={whiteLabel}
      neutralBrandFallback={neutralBrandFallback}
    />
  );
}
